import { useCallback, useState } from "react";
import i18next from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

/**
 * Tracks a dynamic import() so a trigger control can show a spinner for exactly as long as the chunk
 * is being fetched. Whether the code-split feature is currently on screen is only a proxy for that:
 * it stays true long after the fetch finished, which leaves the trigger spinning behind the feature.
 *
 * `load` resolves to false when the chunk could not be fetched — the failure is reported to the user
 * here, so the caller only has to skip whatever it needed the chunk for.
 */
const useLazyChunk = (loadChunk: () => Promise<unknown>) => {
    const [isLoading, setIsLoading] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            await loadChunk();
            return true;
        } catch {
            showErrorToast(i18next.t("common:error.failedToLoadFeature"));
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [loadChunk]);

    return { isLoading, load };
};

export default useLazyChunk;
